// The job page's list: each building's heading (tap to edit the building) with
// its areas under it, and the buildings that differ from a type under that type
// (docs/mockups/buildings-ipad.html §1). Hours beside an area are one copy's;
// a heading shows what the building adds to the job (× its count).
import type { ReactNode } from 'react';
import { CloudUpload } from 'lucide-react';
import { buildingCount, buildingHours, buildingName, type BuildingGroup } from '../lib/buildings';
import type { DeviceRecord } from '../lib/device-db';
import { AddAction } from './add-action';
import { formatHours } from './surface-format';

interface Props {
  groups: readonly BuildingGroup[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** One area's row, as the page shows it on the current tab. */
  renderArea: (area: DeviceRecord) => ReactNode;
  onAddArea: (buildingId: string) => void;
  /** False hides "Area in …" (the Condition tab of a validated visit). */
  canAddArea?: boolean;
  /** Show the buildings' measured hours (the Measure tab only: PM assess is blind, #627; the Condition tab shows ratings instead). */
  showHours: boolean;
  /** A note beside each heading: on the Condition tab, the building's worst rating (block 8). */
  headingNote?: (building: DeviceRecord, areas: readonly DeviceRecord[]) => string;
}

function SyncMark({ record }: { record: DeviceRecord }) {
  if (record.state === 'pending') return <CloudUpload className="h-4 w-4 shrink-0 text-amber-600" aria-label="Waiting to sync" />;
  if (record.state === 'conflict') return <span className="text-xs text-red-700">{record.refusedReason ? 'not saved' : 'conflict'}</span>;
  return null;
}

function Heading({ building, areas, differs, selected, onSelect, showHours, note }: { building: DeviceRecord; areas: readonly DeviceRecord[]; differs: boolean; selected: boolean; onSelect: () => void; showHours: boolean; note?: string }) {
  const count = buildingCount(building);
  const hours = buildingHours(building, areas);
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex min-h-11 w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium ${differs ? 'pl-6' : ''} ${selected ? 'bg-orange-50 text-orange-900' : 'bg-slate-50'}`}
    >
      <span className="min-w-0 flex-1 truncate">
        {differs && <span className="text-slate-400">↳ </span>}
        {buildingName(building)}
      </span>
      {count > 1 && <span className="shrink-0 rounded-full bg-slate-800 px-2 text-xs font-medium text-white">× {count}</span>}
      {note && <span className="shrink-0 text-xs font-normal text-slate-500">{note}</span>}
      {showHours && hours > 0 && <span className="shrink-0 text-xs font-normal tabular-nums text-slate-500">{differs ? '+ ' : ''}{formatHours(hours)}</span>}
      <SyncMark record={building} />
    </button>
  );
}

function Areas({ areas, renderArea, inset }: { areas: readonly DeviceRecord[]; renderArea: Props['renderArea']; inset: boolean }) {
  return (
    <ul className={`divide-y divide-slate-100 ${inset ? 'pl-3' : ''}`}>
      {areas.map((area) => (
        <li key={area.id} className="pl-3">
          {renderArea(area)}
        </li>
      ))}
    </ul>
  );
}

export function BuildingList({ groups, selectedId, onSelect, renderArea, onAddArea, canAddArea = true, showHours, headingNote }: Props) {
  return (
    <div className="divide-y divide-slate-200 overflow-hidden rounded-lg border border-slate-200 bg-white">
      {groups.map((group) => (
        <section key={group.building.id} aria-label={buildingName(group.building)}>
          <Heading building={group.building} areas={group.areas} differs={false} selected={selectedId === group.building.id} onSelect={() => onSelect(group.building.id)} showHours={showHours} note={headingNote?.(group.building, group.areas)} />
          <Areas areas={group.areas} renderArea={renderArea} inset={false} />
          {canAddArea && (
            <AddAction onClick={() => onAddArea(group.building.id)} className="w-full justify-start border-t border-slate-100 pl-6">
              Area in {buildingName(group.building)}
            </AddAction>
          )}
          {group.differing.map(({ building, areas }) => (
            <div key={building.id} className="border-t border-slate-100">
              <Heading building={building} areas={areas} differs selected={selectedId === building.id} onSelect={() => onSelect(building.id)} showHours={showHours} note={headingNote?.(building, areas)} />
              <Areas areas={areas} renderArea={renderArea} inset />
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}
